import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, type User } from "../contexts/AuthContext";
import agreementsService from "../services/agreementsService";
import { analyticsService } from "../services/analyticsService";
import type { HttpError } from "../services/httpService";
import { TrackingEvents, type TrackingEventProps } from "../services/trackingEvents";
import { trackingService } from "../services/trackingService";
import { GATE_EMAIL_PATTERN, useGateNudge, type GateNudgeReason } from "./useGateNudge";
import { INVESTOR_CERTIFICATION_AGREEMENT_NAME, useCertificationSession } from "../components/AccreditedInvestorGate";
import { createShareSignInTradeAlertsState } from "../utils/shareSignInTradeAlerts";

// Cohort tag for the auth funnel. AuthPageV2 stamps `page_version: "v2"`;
// the gate stamps its own value so PostHog can split sign-ups that started on
// the share page from sign-ups that started on /signup.
const SHARE_GATE_PAGE_VERSION = "share-gate-v5" as const;
const RESEND_COOLDOWN_SECONDS = 120;

export type ShareGateStep = "email" | "code";

/**
 * Records the certification disclosed by the sign-in notice, only after
 * authentication succeeds because the agreement endpoint needs the new
 * session. A 409 means the
 * account had already certified, which is the same outcome. Any other
 * failure is reported and swallowed: the person is signed in either way, and
 * the Alpha certification wall still catches an unsigned account before any
 * performance data is shown there. Never bumps the person out of the flow.
 */
export async function recordGateCertification(
  email: string,
  surface: "product_share_gate" | "landing_hero_gate" = "product_share_gate",
): Promise<"signed" | "already_signed" | "failed"> {
  try {
    await agreementsService.signAgreement({
      agreementName: INVESTOR_CERTIFICATION_AGREEMENT_NAME,
      data: {
        signedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
        email,
        source: surface,
      },
    });
    trackingService.track(TrackingEvents.ALPHA_CERTIFICATION_ACKNOWLEDGED, {
      result: "signed",
      surface,
    });
    return "signed";
  } catch (error) {
    const httpError = error as HttpError;
    const status = httpError.status ?? httpError.response?.status ?? null;
    if (status === 409) {
      trackingService.track(TrackingEvents.ALPHA_CERTIFICATION_ACKNOWLEDGED, {
        result: "already_signed",
        surface,
      });
      return "already_signed";
    }
    trackingService.track(TrackingEvents.ALPHA_CERTIFICATION_ERROR, {
      stage: "acknowledgement",
      surface,
      status,
    });
    return "failed";
  }
}

export interface ShareGateFlow {
  productPath: string | null;
  step: ShareGateStep;
  email: string;
  nudge: { reason: GateNudgeReason | null; sequence: number };
  otp: string;
  error: string;
  /** A code is being sent or verified. Drives the "Sending code..." / "Verifying..." labels. */
  isSubmitting: boolean;
  /** A code request or a Google sign-in is in flight: every way in is locked until it settles. */
  isBusy: boolean;
  /** Google's button reports its sign-in in flight, so the email and code paths lock with it. */
  setGoogleBusy: (busy: boolean) => void;
  resendCooldown: number;
  isModalOpen: boolean;
  setEmail: (value: string) => void;
  setOtp: (value: string) => void;
  submitEmail: () => Promise<void>;
  submitCode: () => Promise<void>;
  /** After Google signs the person in: the same chain as a good code. */
  completeGoogleSignIn: (user: User | undefined) => Promise<void>;
  resendCode: () => Promise<void>;
  editEmail: () => void;
  openModal: (ctaId: string) => void;
  closeModal: (ctaId?: string) => void;
}

export interface UseProductShareGateFlowParams {
  /** True while the sign-up panel is on screen for an anonymous viewer. */
  enabled: boolean;
  productPath: string | null;
  total: number;
  trackingProps: TrackingEventProps;
  /** The share link's utm_* values: registered as session tags, with the cohort tags, while the gate is on screen. */
  attribution: Readonly<Record<string, string>>;
  onCtaClick: (ctaId: string, destination: string, details?: TrackingEventProps) => void;
}

/**
 * One state machine for the sign-up that lives on the share page. The page
 * panel and the modal render the same panel component over this state, so a
 * value typed in one place is still there in the other (V5D). The email step
 * can submit from either; the code step is always asked in the modal. A good
 * code sends the person to the shared product in Discover.
 */
export function useProductShareGateFlow({
  enabled,
  productPath,
  total,
  trackingProps,
  attribution,
  onCtaClick,
}: UseProductShareGateFlowParams): ShareGateFlow {
  const navigate = useNavigate();
  const location = useLocation();
  const currentLocationKeyRef = useRef(location.key);
  const { sendOtp, verifyOtp, getDefaultLandingPage, user: authenticatedUser, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { trackPendingCertificationSave } = useCertificationSession();
  const { nudge, pulse, clear } = useGateNudge();
  const [step, setStep] = useState<ShareGateStep>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtpRaw] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Google's credential exchange runs inside GoogleSignInButton; while it is in
  // flight a code request must not start a second, competing auth transition.
  const [isGoogleBusy, setGoogleBusy] = useState(false);
  const isBusy = isSubmitting || isGoogleBusy;
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [pendingLanding, setPendingLanding] = useState<{
    userId: string;
    productPath: string | null;
    locationKey: string;
  } | null>(null);
  const cooldownTimerRef = useRef<number | null>(null);
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    currentLocationKeyRef.current = location.key;
    requestIdRef.current += 1;
    setIsSubmitting(false);
    setGoogleBusy(false);
    setModalOpen(false);
    setStep("email");
    setOtpRaw("");
    setError("");
  }, [location.key]);

  const isCurrentRequest = useCallback((requestId: number) => (
    isMountedRef.current
    && requestIdRef.current === requestId
    && currentLocationKeyRef.current === location.key
  ), [location.key]);

  // A primitive for the effect below to depend on: a render that builds an
  // equal attribution object must not re-run it.
  const attributionKey = JSON.stringify(attribution);

  // Only while the gate is actually on screen (loaded, non-private product,
  // anonymous viewer): tag every auth event emitted meanwhile (AuthContext's
  // OTP_SENT / ERROR / ACCOUNT_CREATED included) with the share-gate cohort
  // and the share link's utm_* attribution, which this page's URL no longer
  // carries. The cohort tags come last so attribution can never override them.
  // Signed-in viewers, private products and bad tokens never enter the cohort.
  useEffect(() => {
    if (!enabled) return undefined;
    const tags = {
      ...(JSON.parse(attributionKey) as Record<string, string>),
      page_version: SHARE_GATE_PAGE_VERSION,
      mode: "signup",
    };
    // posthog-js keeps the visitor's own landing-URL utm_* in this same session store, so registering over a key
    // replaces their value. Note what is there now and put it back on cleanup, rather than deleting it.
    const visitorValues = Object.keys(tags).map((key) => [key, trackingService.getSessionSuperProperty(key)] as const);
    // auth_account_created relies on these tags still being registered when AuthContext's sign-in handler captures it.
    trackingService.registerSessionSuperProperties(tags);
    return () => {
      const restore: TrackingEventProps = {};
      visitorValues.forEach(([key, value]) => {
        if (value === undefined) trackingService.unregisterSuperProperty(key);
        else restore[key] = value;
      });
      if (Object.keys(restore).length > 0) trackingService.registerSessionSuperProperties(restore);
    };
  }, [enabled, attributionKey]);

  // Open the sign-up funnel the way /signup does (auth_signup_start plus the
  // start timestamp that time_to_convert reads), once per gate appearance:
  // keyed on `enabled` alone, so a change of attribution never fires it again.
  // It sits after the tags effect (effects run in declaration order) so the
  // event carries the tags.
  useEffect(() => {
    if (enabled) analyticsService.trackSignupStart();
  }, [enabled]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
      if (cooldownTimerRef.current !== null) window.clearInterval(cooldownTimerRef.current);
    };
  }, []);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimerRef.current !== null) window.clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = window.setInterval(() => {
      setResendCooldown((previous) => {
        if (previous <= 1) {
          if (cooldownTimerRef.current !== null) window.clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  }, []);

  const emailDomain = email.split("@")[1] || "unknown";

  const submitEmail = useCallback(async () => {
    if (isBusy) return;
    if (!GATE_EMAIL_PATTERN.test(email)) {
      pulse("email");
      trackingService.track(TrackingEvents.SHARE_GATE_NUDGE, { ...trackingProps, reason: "email" });
      return;
    }
    const requestId = ++requestIdRef.current;
    setIsSubmitting(true);
    setError("");
    onCtaClick("gate_email_submit", "otp", { email_domain: emailDomain });

    try {
      const result = await sendOtp(email, "signup");
      if (!isCurrentRequest(requestId)) return;
      if (result.success) {
        analyticsService.trackSignupEmailSubmitted(email);
        setStep("code");
        setModalOpen(true);
        startCooldown();
      } else {
        setError(result.message || "We could not send a code. Try again.");
      }
    } catch {
      if (isCurrentRequest(requestId)) setError("We could not send a code. Try again.");
    } finally {
      if (isCurrentRequest(requestId)) setIsSubmitting(false);
    }
  }, [email, emailDomain, isBusy, isCurrentRequest, onCtaClick, sendOtp, startCooldown, pulse, trackingProps]);

  // Successful sign-in accepts the displayed certification. Alpha waits for this save.
  const finishSignIn = useCallback(async (user: User, method: "otp" | "google") => {
    if (!isMountedRef.current || currentLocationKeyRef.current !== location.key) return;
    await trackPendingCertificationSave(user.id, recordGateCertification(user.email || email));
    if (!isMountedRef.current || currentLocationKeyRef.current !== location.key) return;
    analyticsService.trackSignupComplete(user.id, method === "google" ? "google_sso" : "email_otp");
    trackingService.track(TrackingEvents.PRODUCT_SHARE_UNLOCK, {
      ...trackingProps,
      holding_count: total,
      certified_at_gate: true,
      method,
    });
    if (isMountedRef.current && currentLocationKeyRef.current === location.key) {
      setPendingLanding({ userId: user.id, productPath, locationKey: location.key });
    }
  }, [email, location.key, productPath, total, trackingProps, trackPendingCertificationSave]);

  // A delayed sign-in must not redirect a different account or another share.
  useEffect(() => {
    if (!pendingLanding || isAuthLoading || !isAuthenticated) return;
    setPendingLanding(null);
    if (authenticatedUser?.id !== pendingLanding.userId || location.key !== pendingLanding.locationKey) return;
    navigate(pendingLanding.productPath ?? getDefaultLandingPage(), {
      replace: true,
      state: createShareSignInTradeAlertsState(pendingLanding.userId, pendingLanding.productPath),
    });
  }, [authenticatedUser?.id, pendingLanding, getDefaultLandingPage, isAuthLoading, isAuthenticated, location.key, navigate]);

  const submitCode = useCallback(async () => {
    if (otp.length !== 6 || isBusy) return;
    const requestId = ++requestIdRef.current;
    setIsSubmitting(true);
    setError("");
    onCtaClick("gate_code_submit", "app", { email_domain: emailDomain });
    trackingService.track(TrackingEvents.AUTH_SIGNUP_OTP_SUBMITTED, {
      email_domain: emailDomain,
      page_version: SHARE_GATE_PAGE_VERSION,
      mode: "signup",
    });

    try {
      const result = await verifyOtp(email, otp, "signup");
      if (!isCurrentRequest(requestId)) return;
      if (result.success && result.user) {
        await finishSignIn(result.user, "otp");
        return;
      }
      setError(result.message || "That code did not work. Check it and try again.");
    } catch {
      if (isCurrentRequest(requestId)) setError("That code did not work. Check it and try again.");
    } finally {
      if (isCurrentRequest(requestId)) setIsSubmitting(false);
    }
  }, [email, emailDomain, finishSignIn, isBusy, isCurrentRequest, onCtaClick, otp, verifyOtp]);

  const completeGoogleSignIn = useCallback(async (user: User | undefined) => {
    if (!user) return;
    await finishSignIn(user, "google");
  }, [finishSignIn]);

  const resendCode = useCallback(async () => {
    if (isBusy || resendCooldown > 0) return;
    const requestId = ++requestIdRef.current;
    setIsSubmitting(true);
    setError("");
    onCtaClick("gate_resend_code", "otp", { email_domain: emailDomain });
    try {
      const result = await sendOtp(email, "signup");
      if (!isCurrentRequest(requestId)) return;
      if (result.success) startCooldown();
      else setError(result.message || "We could not resend the code. Try again.");
    } catch {
      if (isCurrentRequest(requestId)) setError("We could not resend the code. Try again.");
    } finally {
      if (isCurrentRequest(requestId)) setIsSubmitting(false);
    }
  }, [email, emailDomain, isBusy, isCurrentRequest, onCtaClick, resendCooldown, sendOtp, startCooldown]);

  const editEmail = useCallback(() => {
    onCtaClick("gate_edit_email", "gate");
    setStep("email");
    setOtpRaw("");
    setError("");
  }, [onCtaClick]);

  const openModal = useCallback((ctaId: string) => {
    onCtaClick(ctaId, "gate_modal");
    setModalOpen(true);
  }, [onCtaClick]);

  // Closing always returns to the email step with the typed values kept.
  const closeModal = useCallback((ctaId = "gate_modal_close") => {
    onCtaClick(ctaId, "gate");
    setModalOpen(false);
    setStep("email");
    setOtpRaw("");
    setError("");
  }, [onCtaClick]);

  const setOtp = useCallback((value: string) => {
    setOtpRaw(value.replace(/\D/g, "").slice(0, 6));
  }, []);

  return {
    productPath,
    step,
    email,
    nudge,
    otp,
    error,
    isSubmitting,
    isBusy,
    setGoogleBusy,
    resendCooldown,
    isModalOpen,
    // Trimmed and lowercased on input: the backend rejects any whitespace
    // around the address with "Correct email is required".
    setEmail: (value) => {
      const normalized = value.trim().toLowerCase();
      setEmail(normalized);
      if (GATE_EMAIL_PATTERN.test(normalized)) clear("email");
    },
    setOtp,
    submitEmail,
    submitCode,
    completeGoogleSignIn,
    resendCode,
    editEmail,
    openModal,
    closeModal,
  };
}
